import React, { forwardRef } from 'react';
import styled, { css } from 'styled-components';

/* ================== Types ================== */
type AsideProps = {
  expanded?: boolean;
  durationMs?: number;
  width?: React.CSSProperties['width'];
  minWidth?: React.CSSProperties['minWidth'];
  easing?: React.CSSProperties['transitionTimingFunction'];
  children?: React.ReactNode;
};
type ContainerProps = {
  durationMs?: number;
  easing?: React.CSSProperties['transitionTimingFunction'];
};
type FooterProps = React.HTMLAttributes<HTMLElement> & { expanded?: boolean };

export type LayoutProps = React.HTMLAttributes<HTMLDivElement> &
  ContainerProps & {
    header?: React.ReactNode;
    footer?: React.ReactNode;
    children?: React.ReactNode;
    left?: React.ReactNode;
    right?: React.ReactNode;
    leftProps?: AsideProps;
    rightProps?: AsideProps;
    footerProps?: FooterProps;
  };

/* ================== Grid ================== */

const Container = styled.div<ContainerProps>`
  --gap: 2rem;

  display: grid;
  grid-template-rows: auto 1fr auto;
  grid-template-columns: minmax(0, max-content) 1fr minmax(0, max-content);
  grid-template-areas:
    'header header header'
    'left   main   right'
    'left   footer footer';

  height: 100vh;
  width: 100vw;
  gap: var(--gap);
  overflow: hidden;
  position: relative;

  & > header {
    grid-area: header;
  }
  & > main {
    grid-area: main;
    min-width: 0;
    min-height: 0;
    overflow: auto;
  }
  & > footer {
    grid-area: footer;
  }

  /* Mobile: stack everything */
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    grid-template-rows: auto auto 1fr auto auto;
    grid-template-areas:
      'header'
      'left'
      'main'
      'right'
      'footer';
  }

  @media (prefers-reduced-motion: reduce) {
    & * {
      transition-duration: 0.01ms !important;
      transition-delay: 0ms !important;
    }
  }
`;

/* ============== Asides (collapsible) ============== */

const asideBase = css<AsideProps>`
  ${({ expanded, width = '280px', minWidth = 'auto' }) => `
    width: ${expanded ? width : 0};
    min-width: ${expanded ? minWidth : 0};
  `}
  transition:
    width ${({ durationMs = 300 }) => durationMs}ms
      ${({ easing = 'ease-in-out' }) => easing},
    min-width ${({ durationMs = 300 }) => durationMs}ms
      ${({ easing = 'ease-in-out' }) => easing};
  will-change: width;
  overflow: auto;
  min-height: 0;

  @media (max-width: 768px) {
    width: 100%;
    min-width: 0;
    transition: none;
  }
`;

const LeftAside = styled.aside.withConfig({
  shouldForwardProp: (p) => !['expanded', 'durationMs', 'width', 'easing', 'minWidth'].includes(p),
})<AsideProps>`
  grid-area: left;
  ${asideBase}
`;

const RightAside = styled.aside.withConfig({
  shouldForwardProp: (p) => !['expanded', 'durationMs', 'width', 'easing', 'minWidth'].includes(p),
})<AsideProps>`
  grid-area: right;
  ${asideBase}
`;

const Footer = styled.footer.withConfig({
  shouldForwardProp: (p) => p !== 'expanded',
})<FooterProps>`
  grid-area: footer;
  overflow: hidden;
  transition: height 300ms ease-in-out;
  height: ${({ expanded = true }) => (expanded ? 'fit-content' : 0)}px;
`;

/* ================== Component ================== */

export const Layout = forwardRef<HTMLDivElement, LayoutProps>(
  (
    {
      header,
      footer,
      children,
      left,
      right,
      leftProps,
      rightProps,
      footerProps,
      durationMs,
      easing,
      ...rest
    },
    ref,
  ) => {
    const asideLeftProps: AsideProps = {
      expanded: true,
      width: '300px',
      easing,
      durationMs,
      ...leftProps,
    };
    const asideRightProps: AsideProps = {
      expanded: false,
      width: '300px',
      easing,
      durationMs,
      ...rightProps,
    };

    return (
      <Container ref={ref} durationMs={durationMs} easing={easing} {...rest}>
        <header>{header}</header>

        <LeftAside {...asideLeftProps}>{left}</LeftAside>
        <main>{children}</main>
        <RightAside {...asideRightProps}>{right}</RightAside>

        <Footer {...footerProps}>{footer}</Footer>
      </Container>
    );
  },
);
